import { Formik } from 'formik';
import * as Yup from "yup";
import React, { useState, useRef } from 'react';
import { Alert, Button, Col, Form, Input, InputGroup, Label, Row, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { PostData } from '../../../services/FetchData';
import { useNavigate } from 'react-router-dom';

const CollectAiAssistantMagento = () => {
	const [errorMessages, setErrorMessages] = useState("");
	const [selectedFile, setSelectedFile] = useState(null);
	const [fileInputKey, setFileInputKey] = useState(0);
	const fileInputRef = useRef(null);
	const navigate = useNavigate();

	const handleFileChange = (event) => {
		const file = event.target.files[0];
		if (file) {
			// Validate file type
			if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
				setSelectedFile(file);
				setErrorMessages(""); // Clear any previous errors
			} else {
				setErrorMessages("Please select a valid CSV file");
				if (fileInputRef.current) {
					fileInputRef.current.value = "";
				}
			}
		}
	};

	const handleRemoveFile = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setSelectedFile(null);
		setErrorMessages("");
		// Force React to re-render the input by changing the key
		setFileInputKey(prev => prev + 1);
	};

	const saveStoreInformation = (values, actions) => {
		// If a CSV file is selected, include it in FormData
		if (selectedFile) {
			const formData = new FormData();
			formData.append("storeURL", values.websiteURL);
			formData.append("openApiKey", values.openApiKey);
			formData.append("storeType", "magento");
			formData.append("productSkuFile", selectedFile);
			// Flag to indicate fetching only specific SKUs from the file
			formData.append("fetchAllProducts", "false");

			PostData("ms1", "api/v1/website/saveAiAssistantStore", formData, true).then(result => {
				if (result !== "Invalid" && result !== undefined) {
					if (result.status === "success") {
						// Navigate to settings page with websiteKeys and URL
						navigate("/getStarted/aiAssistantMagentoSettings", { 
							state: { 
								websiteKeys: result.data.websiteKeys,
								storeURL: values.websiteURL
							},
							replace: true 
						});
					} else if (result.status === "failure") {
						setErrorMessages(result.message)
					} else {
						setErrorMessages(result.message.message)
					}
				}
				actions.setSubmitting(false)
			}).catch(() => {
				setErrorMessages("An error occurred. Please try again.");
				actions.setSubmitting(false)
			});
		} else {
			// No file selected, fetch ALL products from the store
			const requestData = {
				storeURL: values.websiteURL,
				openApiKey: values.openApiKey,
				storeType: "magento",
				fetchAllProducts: true // Flag to indicate fetching all products from Magento store
			};
			
			PostData("ms1", "api/v1/website/saveAiAssistantStore", requestData).then(result => {
				if (result !== "Invalid" && result !== undefined) {
					if (result.status === "success") {
						// Navigate to settings page with websiteKeys and URL
						navigate("/getStarted/aiAssistantMagentoSettings", { 
							state: { 
								websiteKeys: result.data.websiteKeys,
								storeURL: values.websiteURL
							},
							replace: true 
						});
					} else if (result.status === "failure") {
						setErrorMessages(result.message)
					} else {
						setErrorMessages(result.message.message)
					}
				}
				actions.setSubmitting(false)
			})
		}
	}

	return (
		<>
			<section className='choosePlatformHeader'>
				<div className='container'>
					<Row className="align-items-center justify-content-center">
						<Col xs={"auto"}>
							<h2 className='mt-5'>Store Information</h2>
						</Col>
					</Row>
				</div>
			</section>
			<section className='shopifyAppInstallationGuide mt-5'>
				<div className='container'>
					<Row className='align-items-center justify-content-center'>
						<Col xs={12} lg={6} className='text-center d-none d-lg-block'>
							<img src={require("./../../../assets/images/integration.png")} alt="integration" className='img-fluid' />
						</Col>
						<Col xs={12} lg={6}>
							{errorMessages !== "" && (
								<Alert color="danger">
									{errorMessages}
								</Alert>
							)}
							<div className='storeInformationWrapper'>
								<Formik
									initialValues={{ openApiKey: "", websiteURL: "" }}
									onSubmit={(values, actions) => {
										saveStoreInformation(values, actions)
									}}
									validationSchema={Yup.object().shape({
										openApiKey: Yup.string().required("Open API key is required"),
										websiteURL: Yup.string().required("Website URL is required").url("Please enter valid website URL")
									})}
								>
									{props => {
										const {
											values,
											touched,
											errors,
											isSubmitting,
											handleChange,
											handleBlur,
											handleSubmit
										} = props;
										return (
											<Form role="form" onSubmit={handleSubmit} noValidate>
												<div className={`position-relative mb-5 ${(errors.openApiKey && touched.openApiKey && "error_field")}`}>
													<Label className="input-group-alternative-label" >
														Open API key
													</Label>
													<InputGroup className="input-group-alternative">
														<Input
															placeholder="Open API key"
															type={"text"}
															name="openApiKey"
															value={values.openApiKey}
															onChange={handleChange}
															onBlur={handleBlur}
															className={
																"col" ||
																(errors.openApiKey && touched.openApiKey && "error")
															}
															autofill="false"
														/>
													</InputGroup>
													{errors.openApiKey && touched.openApiKey && (
														<div className="input-feedback">{errors.openApiKey}</div>
													)}
												</div>
												<div className={`position-relative mb-5 ${(errors.websiteURL && touched.websiteURL && "error_field")}`}>
													<Label className="input-group-alternative-label" >
														Website URL
													</Label>
													<InputGroup className="input-group-alternative">
														<Input
															placeholder="Website URL"
															type={"text"}
															name="websiteURL"
															value={values.websiteURL}
															onChange={handleChange}
															onBlur={handleBlur}
															className={
																"col" ||
																(errors.websiteURL && touched.websiteURL && "error")
															}
															autofill="false"
														/>
													</InputGroup>
													{errors.websiteURL && touched.websiteURL && (
														<div className="input-feedback">{errors.websiteURL}</div>
													)}
												</div>
												<div className={`position-relative mb-5`}>
													<Label className="input-group-alternative-label" >
														Product SKU
													</Label>
													<InputGroup className="input-group-alternative position-relative">
														<Input
															key={fileInputKey}
															ref={fileInputRef}
															type="file"
															accept=".csv"
															onChange={handleFileChange}
															className="col"
															style={selectedFile ? { paddingRight: '40px' } : {}}
														/>
														{selectedFile && (
															<Button
																color="link"
																className="p-0"
																onClick={handleRemoveFile}
																style={{ 
																	position: 'absolute',
																	right: '12px',
																	top: '50%',
																	transform: 'translateY(-50%)',
																	fontSize: '16px', 
																	color: '#dc3545',
																	border: 'none',
																	background: 'transparent',
																	padding: '0',
																	zIndex: 10,
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'center',
																	width: '20px',
																	height: '20px'
																}}
																title="Remove file"
															>
																<FontAwesomeIcon icon={faTimes} />
															</Button>
														)}
													</InputGroup>
													{selectedFile && (
														<small className="text-muted mt-1 d-block">
															Selected: {selectedFile.name}
														</small>
													)}
												</div>
												<div className="d-flex justify-content-center">
													{isSubmitting ? (
														<Button className="cta authCta" color="primary" type="button" disabled>
															<Spinner
																className="white mr-1"
																size="sm"
																color="light"
															/>
															<span className="ml-2 d-inline-block">Loading</span>
														</Button>
													) : (
														<Button className=" cta authCta " color="primary" type="submit">Save</Button>
													)}
												</div>
											</Form>)
									}}
								</Formik>
							</div>
						</Col>
					</Row>
				</div>
			</section>
		</>
	);
}

export default CollectAiAssistantMagento;

